import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FileText, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { patientQueries } from '@/features/patient-record/queries';
import type { DocumentViewModel } from '@/features/patient-record/types';

import { ClinicalSection, type ClinicalSectionLabels } from '../ClinicalSection';
import { AttachmentPreview } from './AttachmentPreview';
import { DeleteDocumentDialog } from './DeleteDocumentDialog';
import { DocumentComposer } from './DocumentComposer';
import { DocumentRow } from './DocumentRow';
import { buildAttachmentDownloadUrl } from './attachmentUrl';

interface DocumentsSectionProps {
  readonly guid: string;
}

export function DocumentsSection({ guid }: DocumentsSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ ...patientQueries.documents(guid), placeholderData: keepPreviousData });
  const [composerOpen, setComposerOpen] = useState(false);
  const [viewing, setViewing] = useState<DocumentViewModel | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const labels: ClinicalSectionLabels = {
    title: t('patient.documents.heading'),
    empty: t('patient.documents.empty'),
    emptyIcon: <FileText className="h-5 w-5" aria-hidden="true" />,
    loading: t('patient.documents.loading'),
    errorHeading: t('patient.documents.error.heading'),
    errorMessage: t('patient.documents.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="documents"
        labels={labels}
        query={query}
        summary={(query.data ?? []).map((d) => ({ label: d.title, value: d.fileName ?? '—', filled: true }))}
        itemKey={(d) => d.id}
        renderItem={(d) => (
          <DocumentRow
            document={d}
            onView={() => {
              setViewing(d);
            }}
            onDelete={() => {
              setDeletingId(d.id);
            }}
          />
        )}
        headerAction={
          // PTV2-1300: hide the trigger while the inline composer is open.
          composerOpen ? undefined : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setComposerOpen(true);
              }}
              aria-expanded={composerOpen}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              {t('patient.documents.upload')}
            </Button>
          )
        }
        toolbar={
          composerOpen ? (
            <DocumentComposer
              guid={guid}
              onCancel={() => {
                setComposerOpen(false);
              }}
              onSaved={() => {
                setComposerOpen(false);
              }}
            />
          ) : undefined
        }
      />
      {viewing !== null && (
        <AttachmentPreview
          document={viewing}
          downloadUrl={buildAttachmentDownloadUrl(viewing.id)}
          onClose={() => {
            setViewing(null);
          }}
        />
      )}
      {deletingId !== null && (
        <DeleteDocumentDialog
          guid={guid}
          documentId={deletingId}
          onClose={() => {
            setDeletingId(null);
          }}
        />
      )}
    </>
  );
}
